import { useQuery } from "@tanstack/react-query";

import api, { ApiCatalog } from "@/api";
import { Teacher } from "@/types/models/User/v3/kinds/Teacher.interface";
import { useGetUser } from "@/users/v3";

export interface TeacherSearchQueryParams {
  limit: number;
  offset: number;
  orderBy?: string;
  payload?: {
    search: string;
    school_id: number;
  };
  filterSelfUser?: boolean;
  tsOptions?: any;
}

/**
 * @description Fetches teachers on same school based on the search query
 * @example const { data: { results: teachers }, loading: loadingTeacherSearch, error: teacherSearchError } = useTeacherSearchQuery()
 * @param TeacherSearchQueryParams
 * @param {Number} TeacherSearchQueryParams.limit - Limit of teachers to be fetched
 * @param {Number} TeacherSearchQueryParams.offset - Limit of teachers to be fetched
 * @param {{ search: string, school_id: number }} TeacherSearchQueryParams.payload - Payload for the query
 * @param {Boolean} TeacherSearchQueryParams.filterSelfUser - Filter the user itself and return only colleagues
 * @param TeacherSearchQueryParams.tsOptions - Config for the 🏝️ useQuery
 * @returns UseQueryResult<Teacher[] | null, Error, loading: boolean>
 */
const useTeacherSearchQuery = ({
  limit,
  offset,
  orderBy,
  payload,
  filterSelfUser,
  tsOptions,
}: TeacherSearchQueryParams) => {
  const queryParams = {
    limit,
    search: payload?.search,
    ordering: orderBy,
    offset,
  };
  const { data: user, error } = useGetUser();
  const schoolId = payload?.school_id || payload?.school_id;

  const query = useQuery<{ results: Teacher[] } | null>({
    queryKey: [
      ApiCatalog.teacherThroughSchool.key,
      "search",
      schoolId,
      payload?.search,
      offset,
      limit,
      orderBy,
    ],
    queryFn: async () =>
      await api()
        .addResponseInterceptor<{ results: Teacher[] }>(
          async ({ response, data }) => {
            if (filterSelfUser) {
              const filteredData = data?.results?.filter(
                (teacher) => teacher.user !== user?.unique_id,
              );

              return { response, data: { ...data, results: filteredData } };
            }

            return { response, data };
          },
        )
        .get(ApiCatalog.teacherThroughSchool.endpoint, {
          queryParams,
          urlReplacers: [
            {
              school_id: schoolId,
              teacher_id: null,
            },
          ],
        }),
    staleTime: 60 * 1000, // Stales the query for only a minute, if not, user will not see recently invited colleagues
    retry: false,
    enabled: !!user && !error,
    ...tsOptions,
  });

  return { loading: query.isLoading, ...query };
};

export default useTeacherSearchQuery;
